/**
 * Generic `{token}` fill over an HTML string.
 *
 * Extracted rather than reused from `replacePlaceholdersInTemplate`
 * (server/features/scopingAi/routes/scopingAi.ts): that function operates on a
 * GrapesJS page *object*, not an HTML string, and carries tender-only machinery
 * — `{contract_term}` page-section detection, hidden `__TERM_N__` pairing
 * stamps, per-site page cloning, `{rN_*}` cost-placeholder resolution. None of
 * it applies to a bill validation report, and all of it would have to be
 * skipped around. This is the generic half of the job, on its own.
 */

/** Matches `{token}` and `{{token}}` — template authors write both. */
export const PLACEHOLDER_PATTERN = /\{\{?\s*([a-z][a-z0-9_]*)\s*\}?\}/gi;

/** Every distinct token name present in the template, in first-seen order. */
export function detectPlaceholders(html: string): string[] {
  const seen: string[] = [];
  for (const match of html.matchAll(PLACEHOLDER_PATTERN)) {
    const key = match[1].toLowerCase();
    if (!seen.includes(key)) seen.push(key);
  }
  return seen;
}

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Values land inside an HTML document, and they come from client-supplied data
 * (site names, adviser free-text notes). Escaping is not optional — an
 * apostrophe in a trading name should not be able to break the markup, and a
 * `<` in a note should not be able to inject an element.
 *
 * Newlines become `<br>` so multi-line adviser notes keep their shape, which is
 * what `whitespace-pre-line` does for the same text on screen.
 */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESCAPES[c]).replace(/\r?\n/g, '<br>');
}

export type TokenFillResult = {
  html: string;
  /** Tokens in the template that the data had no value for. */
  missing: string[];
};

/**
 * Replaces every `{token}` with its value from `values`.
 *
 * Unknown tokens are left in place rather than blanked: a template that still
 * literally reads `{variance_dollars}` is an obvious defect the moment anyone
 * looks at the preview, whereas an empty gap reads like a legitimately blank
 * field and can survive review. They are also reported in `missing` so the
 * caller can surface them before anything is sent.
 *
 * Values are pre-formatted strings by contract (see `BillReportData`) — this
 * function does no number or date formatting of its own.
 *
 * `rawKeys` names the handful of tokens whose values are markup the report
 * builds itself (the comparison table rows, the line-item rows) rather than
 * data read off a record. Everything not in that list is escaped, so a trading
 * name containing `&` or an adviser note containing `<` cannot alter the
 * document's structure.
 */
export function fillTokens(
  html: string,
  values: Record<string, string>,
  options: { escape?: boolean; rawKeys?: string[] } = {},
): TokenFillResult {
  const escapeByDefault = options.escape !== false;
  const raw = new Set((options.rawKeys ?? []).map((k) => k.toLowerCase()));
  const missing: string[] = [];

  const filled = html.replace(PLACEHOLDER_PATTERN, (whole, rawKey: string) => {
    const key = rawKey.toLowerCase();
    const value = values[key];
    if (value === undefined || value === null) {
      if (!missing.includes(key)) missing.push(key);
      return whole;
    }
    return escapeByDefault && !raw.has(key) ? escapeHtml(value) : value;
  });

  return { html: filled, missing };
}
